
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../common/Header";
import {
  ArrowLeft,
  User,
  Building,
  Music,
  Calendar,
  Search,
  Copy,
  Lock,
  Edit,
  Unlock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Package,
  Eye,
  Mail,
  Phone,
  MapPin,
  Clock,
  Globe,
  Tag,
  Volume2,
  Target,
  CheckCircle,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { enableEditingLyrics, getOrderById } from "../../redux/reduxSlices/ordersSlice";
import LyricsUploadPopup from "./LyricsUploadPopup";
import { getUserProfile } from "../../redux/reduxSlices/userProfileSlice";
import toast from "react-hot-toast";
import {
  getfinalUploadedAudiobyId,
  getWatermarkUploadedAudioById,
} from "../../redux/reduxSlices/audioUploadSlice";
import EditOrderPopup from "../order/EditOrderPopup";
import { getUsersList } from "../../redux/reduxSlices/adminSlice";
import { coOrdinatorId } from "../../utils/util";

function MusicCreatorOrderDetails({ loginUser, onLogout }) {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { orderByIdData } = useSelector((state) => state.orders);
  const { user } = useSelector((state) => state.profile);

  const { getFinalAudioById } = useSelector((state) => state.audioUpload);
  const {
    adminLoading,
    adminFetchError,
    usersListResponse,
  } = useSelector((state) => state.admin);
  const { getAudioById } = useSelector((state) => state.audioUpload);

  const [submitting, setSubmitting] = useState(false);
  const [isEditingLyrics, setIsEditingLyrics] = useState(false);
  const [lyricsForEditing, setLyricsForEditing] = useState();
  const [uploadLyricsPopup, setUploadLyricsPopup] = useState(false);
  const [enableEditingPopup, setEnableEditingPopup] = useState(false);
  const [leadGenerator, setLeadGenerator] = useState("")
  const [finalSongs, setFinalSongs] = useState(0);
  const [watermarkSongs, setWatermarkSongs] = useState(0);
  const [showEditPopup, setShowEditPopup] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    contactInfo: true,
    companyInfo: true,
    brandInfo: false,
    productionDetails: true,
    projectTimeline: true,
    lyricsVersions: true,
  });

  useEffect(() => {
    if (orderId) {
      dispatch(getWatermarkUploadedAudioById(orderId));
      dispatch(getfinalUploadedAudiobyId(orderId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (getFinalAudioById && Array.isArray(getFinalAudioById.songs)) {
      setFinalSongs(getFinalAudioById.songs.length);
    } else {
      setFinalSongs(0);
    }

    if (getAudioById && Array.isArray(getAudioById.songs)) {
      setWatermarkSongs(getAudioById.songs.length);
    } else {
      setWatermarkSongs(0);
    }
  }, [getFinalAudioById, getAudioById]);

  useEffect(() => {
    if (orderId) dispatch(getOrderById(orderId));
  }, [dispatch, orderId]);

  useEffect(() => {
    dispatch(getUserProfile());
    dispatch(getUsersList())
  }, [dispatch]);

  useEffect(() => {
    const salesPerson = usersListResponse?.sales_persons?.find(
      (person) => person.user_pk === orderByIdData?.sales_person
    );

    if (salesPerson) {
      setLeadGenerator(`${salesPerson.first_name[0].toUpperCase()}${salesPerson.first_name.slice(1)} ${salesPerson.last_name[0].toUpperCase()}${salesPerson.last_name.slice(1)}`);
    }
  }, [usersListResponse, orderByIdData]);

  const handleSubmit = async () => {
    setUploadLyricsPopup(true);
  };

  const handleEditLyrics = (l) => {
    setIsEditingLyrics(true);
    setLyricsForEditing(l);
    setUploadLyricsPopup(true);
  };

  const handleEnableEditing = () => {
    setEnableEditingPopup(true);
  };

  const confirmEnableEditing = async () => {
    try {
      const payload = {
        order_id: orderId,
        lyric_id: orderByIdData?.lyrics?.approved_lyric_id,
      };
      const response = await dispatch(enableEditingLyrics(payload)).unwrap();
      if (response?.success) {
        await dispatch(getOrderById(orderId));
        toast.success("Lyrics editing enabled successfully!");
      } else {
        toast.error("Failed to enable editing. Please try again.");
      }
    } catch (error) {
      toast.error("Failed to enable editing. Please try again.");
    } finally {
      setEnableEditingPopup(false);
    }
  };

  const handleCopyShareUrl = (url) => {
    if (!url) return;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(url)
        .then(() => toast.success("Share URL copied to clipboard!"))
        .catch(() => fallbackCopy(url));
    } else {
      fallbackCopy(url);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      toast.success("Share URL copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy URL");
    }

    document.body.removeChild(textArea);
  };

  // -------- Timeline helpers (kept same, just safer) ----------
  const isStageActive = (status) => status !== "Pending";
  const getDisplayStatus = (status) => status;

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "text-green-600";
      case "In Progress":
        return "text-orange-600";
      case "Pending":
      default:
        return "text-gray-400";
    }
  };

  const getLyricsStageStatus = (currentStatus) => {
    const inLyricsProgress = ["pending", "in_progress", "lyrics_approval_pending"];
    const lyricsDoneOrLater = ["lyrics_approved", "music_approval_pending", "music_approved", "delivered"];

    if (inLyricsProgress.includes(currentStatus)) {
      return currentStatus === "pending" ? "Pending" : "In Progress";
    }

    if (lyricsDoneOrLater.includes(currentStatus)) {
      return "Completed";
    }

    return "Pending";
  };

  const getProductionStageStatus = (currentStatus) => {
    const productionInProgress = ["lyrics_approved", "music_approval_pending"];
    const productionDoneOrLater = ["music_approved", "delivered"];

    if (productionInProgress.includes(currentStatus)) return "In Progress";
    if (productionDoneOrLater.includes(currentStatus)) return "Completed";
    return "Pending";
  };

  const getReviewStageStatus = (currentStatus) => {
    if (currentStatus === "music_approval_pending") return "In Progress";
    if (["music_approved", "delivered"].includes(currentStatus)) return "Completed";
    return "Pending";
  };

  const getDeliveryStageStatus = (currentStatus) => {
    return currentStatus === "delivered" ? "Completed" : "Pending";
  };

  const getTimelineStages = (statusObj) => {
    const current = statusObj?.status || "pending";

    const timelineStages = [
      { stage: "order-received", label: "Order Received", status: "Completed" },
      { stage: "lyrics", label: "Lyrics Submission", status: getLyricsStageStatus(current) },
      { stage: "music-production", label: "Music Production", status: getProductionStageStatus(current) },
      { stage: "client-review", label: "Client Review", status: getReviewStageStatus(current) },
      { stage: "final-delivery", label: "Final Delivery", status: getDeliveryStageStatus(current) },
    ];

    return timelineStages.map((stage) => ({
      ...stage,
      isActive: isStageActive(stage.status),
      displayStatus: getDisplayStatus(stage.status),
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const AccordionCard = ({ title, icon: Icon, children, className = "", section }) => (
    <div className={`bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors ${className}`}>
      <button
        onClick={() => toggleSection(section)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gradient-to-br from-orange-500 to-orange-600 rounded">
            <Icon className="w-4 h-4 text-white" />
          </div>
          <h2 className="text-base font-semibold text-gray-800">{title}</h2>
        </div>
        <div className="p-1 hover:bg-gray-100 rounded transition-colors">
          {expandedSections[section] ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>

      <div
        className={`px-4 transition-all duration-200 overflow-hidden ${expandedSections[section] ? "pb-4 opacity-100 max-h-[2000px]" : "pb-0 opacity-0 max-h-0"
          }`}
      >
        <div className="border-t border-gray-100 pt-4">{children}</div>
      </div>
    </div>
  );

  const InfoField = ({ label, value, icon: Icon, className = "" }) => (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span>{label}</span>
      </div>
      <div className="text-sm text-gray-800 break-words">{value || "-"}</div>
    </div>
  );

  // ------- Helpers for flat fields -------
  const joinOrDash = (v) => (Array.isArray(v) && v.length ? v.join(", ") : v || "-");
  const keywordsOrDash = (v) => (Array.isArray(v) && v.length ? v.join(", ") : v || "-");

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={loginUser} onLogout={onLogout} />
      <div className="mx-auto px-4 py-4">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-4 max-md:flex-col">
          <div className="flex items-center gap-3 max-md:w-full max-md:mb-3 max-md:justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-gray-600 hover:text-gray-800 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-medium">Back</span>
            </button>

            <div>
              <h1 className="text-lg font-bold text-gray-900">Order Details</h1>
              <div className="text-xs text-gray-500">ID: {orderId}</div>
              <div className="text-xs text-gray-500">Lead Generated by : {leadGenerator}</div>
              <h3 className="text-lg font-bold text-gray-900">Song Duration: {orderByIdData?.song_duration}</h3>
            </div>
            <div>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {loginUser?.user_type === "sales_person" && (
              <button
                className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
                onClick={() => setShowEditPopup(true)}
              >
                <Edit className="w-3.5 h-3.5 max-md:hidden" />
                Edit Order
              </button>
            )}

            <button
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
              onClick={() => navigate(`/watermarked-jingles/${orderId}`)}
              disabled={watermarkSongs <= 0}
            >
              <Package className="w-3.5 h-3.5 max-md:hidden" />
              Watermarked ({watermarkSongs})
            </button>

            <button
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
              onClick={() => navigate(`/jingles-delivery/${orderId}`)}
              disabled={finalSongs <= 0}
            >
              <Package className="w-3.5 h-3.5 max-md:hidden" />
              Final ({finalSongs})
            </button>

            <button
              className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
              onClick={() => navigate(`/audio-upload/${orderId}`)}
              disabled={user?.user_type === "sales_person" || submitting || orderByIdData?.lyrics?.locked === false}
            >
              <Music className="w-3.5 h-3.5 max-md:hidden" />
              Upload Audio
            </button>
          </div>
        </div>

        {orderByIdData && Object.keys(orderByIdData).length ? (
          <>
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              {/* Left Column */}
              <div className="space-y-4">
                <AccordionCard title="Contact Information" icon={User} section="contactInfo">
                  <div className="space-y-3">
                    <InfoField label="Full Name" value={orderByIdData?.name} icon={User} />
                    <InfoField label="Email Address" value={orderByIdData?.email} icon={Mail} />
                    <InfoField label="Mobile Number" value={orderByIdData?.mobile_number} icon={Phone} />
                  </div>
                </AccordionCard>

                <AccordionCard title="Company Information" icon={Building} section="companyInfo">
                  <div className="space-y-3">
                    <InfoField label="Company Name" value={orderByIdData?.company_name} icon={Building} />
                    <InfoField label="Company Website" value={orderByIdData?.website} icon={Globe} />
                    <InfoField label="GST Number" value={orderByIdData?.gst_number} icon={Tag} />
                    <InfoField label="Address" value={orderByIdData?.company_address} icon={MapPin} />
                    <InfoField label="City" value={orderByIdData?.company_city} icon={MapPin} />
                    <InfoField label="Instagram Link" value={orderByIdData?.instagram_link} icon={Globe} />
                  </div>
                </AccordionCard>
              </div>

              {/* Middle Column */}
              <div className="space-y-4">
                <AccordionCard title="Brand Information" icon={Globe} section="brandInfo">
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                    <InfoField label="Brand/Company Name (in Jingle)" value={orderByIdData?.brand_name_in_jingle} icon={Tag} />
                    <InfoField label="Company Details" value={orderByIdData?.company_details} icon={Building} />
                    <InfoField
                      label="Target Audience"
                      value={joinOrDash(orderByIdData?.target_audience)}
                      icon={Target}
                    />
                    <InfoField label="Brand Category" value={orderByIdData?.brand_category} icon={Tag} />
                    <InfoField label="Tagline/Slogan" value={orderByIdData?.tagline_or_slogan} icon={Tag} />
                    <InfoField
                      label="Keywords to Highlight"
                      value={keywordsOrDash(orderByIdData?.keywords_to_highlight)}
                      icon={Tag}
                    />
                    <InfoField label="Restrictions" value={orderByIdData?.restrictions} icon={Lock} />
                    <InfoField label="Preferred Language" value={orderByIdData?.preferred_language} icon={Globe} />
                    <InfoField label="Jingle Requirements" value={orderByIdData?.jingle_requirements} icon={Music} />
                    <InfoField label="Desired Tone" value={orderByIdData?.tone} icon={Volume2} />
                    <InfoField label="Voice Preference" value={orderByIdData?.voice_preference} icon={User} />
                  </div>
                </AccordionCard>

                <AccordionCard title="Production Details" icon={Music} section="productionDetails">
                  <div className="space-y-3">
                    <InfoField label="Timeline" value={orderByIdData?.timeline} icon={Clock} />
                    <InfoField label="Preferred Language" value={orderByIdData?.preferred_language} icon={Globe} />
                    <InfoField label="Tone" value={orderByIdData?.tone} icon={Volume2} />
                    <InfoField label="Voice Preference" value={orderByIdData?.voice_preference} icon={User} />
                    <InfoField label="Target Audience" value={joinOrDash(orderByIdData?.target_audience)} icon={Target} />
                    <InfoField label="Assigned To" value={orderByIdData?.assigned_to} icon={User} />
                    <InfoField
                      label="Client Form Submitted"
                      value={orderByIdData?.client_form_submitted ? "Yes" : "No"}
                      icon={CheckCircle}
                    />
                  </div>
                </AccordionCard>

                <AccordionCard title="Project Timeline" icon={Calendar} section="projectTimeline">
                  <div className="space-y-3">
                    {getTimelineStages(orderByIdData?.status).map((stage, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className={stage.isActive ? "text-gray-600" : "text-gray-400"}>{stage.label}</span>
                        <span className={`${getStatusColor(stage.status)} font-medium`}>{stage.displayStatus}</span>
                      </div>
                    ))}
                  </div>
                </AccordionCard>
              </div>

              {/* Right Column - Lyrics */}
              <div className="space-y-4">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between p-4 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <Music className="w-4 h-4 text-orange-500" />
                      <h3 className="font-semibold text-gray-900">Lyrics Versions</h3>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
                        onClick={handleSubmit}
                        disabled={user?.user_type === "sales_person" || submitting || orderByIdData?.lyrics?.locked}
                      >
                        {submitting ? (
                          <>
                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Music className="w-3.5 h-3.5" />
                            Upload Lyrics
                          </>
                        )}
                      </button>

                      {orderByIdData?.lyrics?.items?.length > 0 && (
                        <>
                          <button
                            className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
                            onClick={() => navigate(`/lyrics/${orderId}`)}
                            disabled={submitting}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            View Lyrics Page
                          </button>

                          <button
                            onClick={() => handleCopyShareUrl(`${window.location.origin}/lyrics/${orderId}`)}
                            className="inline-flex items-center gap-1.5 bg-gradient-to-br from-orange-500 to-orange-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-50"
                          >
                            <Copy className="w-3.5 h-3.5" />
                            Copy Share URL
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    {orderByIdData?.lyrics?.locked && user?.user_type === "sales_person" && coOrdinatorId.includes(user?.user_pk) && (
                      <div className="mb-3">
                        <button
                          onClick={handleEnableEditing}
                          className="px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded font-medium hover:from-blue-600 hover:to-blue-700 transition-colors text-sm flex items-center gap-1.5"
                        >
                          <Unlock className="w-3.5 h-3.5" />
                          Enable Lyrics Editing
                        </button>
                      </div>
                    )}

                    {orderByIdData?.lyrics?.items?.length > 0 ? (
                      <div className="grid grid-cols-1 gap-3">
                        {orderByIdData?.lyrics?.items?.map((l, i) => {
                          const isLocked = orderByIdData?.lyrics?.locked;
                          const isApproved = isLocked && orderByIdData?.lyrics?.approved_lyric_id === l.lyric_id;
                          const isOtherLockedVersion = isLocked && !isApproved;
                          const isEditable = !isLocked;

                          return (
                            <div
                              key={l.lyric_id}
                              className={`space-y-2 p-3 rounded border overflow-auto ${isApproved
                                ? "bg-green-50 border-green-200"
                                : isOtherLockedVersion
                                  ? "bg-gray-50 border-gray-200 opacity-80"
                                  : "bg-white border-gray-200"
                                }`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-700">Version {i + 1}</span>

                                  {isApproved && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-800 rounded text-xs">
                                      <CheckCircle className="w-3 h-3" />
                                      <span>Approved</span>
                                    </div>
                                  )}

                                  {isLocked && !isApproved && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                      <Lock className="w-3 h-3" />
                                      <span>Locked</span>
                                    </div>
                                  )}
                                </div>

                                {isEditable && (
                                  <button
                                    onClick={() => handleEditLyrics(l)}
                                    className="px-2 py-1 bg-orange-50 hover:bg-orange-100 text-orange-700 border border-orange-200 rounded text-xs font-medium flex items-center gap-1 transition-colors"
                                  >
                                    <Edit className="w-3 h-3" />
                                    Edit
                                  </button>
                                )}
                              </div>

                              <div className={`text-sm text-gray-800 ${isOtherLockedVersion ? "opacity-75" : ""}`}>
                                <pre className="whitespace-pre-wrap font-sans leading-relaxed max-h-48 overflow-y-auto">
                                  {l.text}
                                </pre>
                              </div>

                              <div className="pt-2 border-t border-gray-100">
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    <span>{l.created_by}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Music className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                        <h3 className="text-sm font-medium text-gray-500 mb-1">No lyrics uploaded yet</h3>
                        <p className="text-xs text-gray-400">Upload lyrics to get started</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="max-w-md mx-auto">
              <div className="mb-4">
                <Search className="w-12 h-12 text-gray-400 mx-auto" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Order Not Found</h2>
              <p className="text-sm text-gray-600">
                The order you're looking for doesn't exist or may have been removed.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {uploadLyricsPopup && (
        <LyricsUploadPopup
          uploadLyricsPopup={uploadLyricsPopup}
          setUploadLyricsPopup={setUploadLyricsPopup}
          orderId={orderId}
          isEditingLyrics={isEditingLyrics}
          setIsEditingLyrics={setIsEditingLyrics}
          lyricsForEditing={lyricsForEditing}
          setLyricsForEditing={setLyricsForEditing}
          onAfterSubmit={async () => {
            await dispatch(getOrderById(orderId));
          }}
        />
      )}

      {enableEditingPopup && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-sm w-full">
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Enable Editing</h3>
                  <p className="text-xs text-gray-600">Confirm to unlock lyrics</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-3">
                <div className="flex items-start gap-2">
                  <Unlock className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-xs mb-1">What happens?</h4>
                    <ul className="text-xs text-gray-700 space-y-1">
                      <li>• All versions become editable</li>
                      <li>• New versions can be uploaded</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 py-3 bg-gray-50 rounded-b-lg flex justify-end gap-2">
              <button
                onClick={() => setEnableEditingPopup(false)}
                className="px-3 py-1.5 border border-gray-300 text-gray-700 font-medium rounded text-sm hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmEnableEditing}
                className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium rounded text-sm flex items-center gap-1.5 transition-colors"
              >
                <Unlock className="w-3.5 h-3.5" />
                Enable
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditPopup && (
        <EditOrderPopup
          orderId={orderId}
          onClose={() => setShowEditPopup(false)}
          onSave={() => {
            dispatch(getOrderById(orderId));
          }}
        />
      )}
    </div>
  );
}

export default MusicCreatorOrderDetails;